/**
 * ProjectContextPane - the facts of the project, always to hand.
 *
 * These read off the Details tab before: status, priority, dates, the owner
 * tile and the "N members" tile. That made them invisible the moment someone
 * opened Resources or the Feed, which is exactly when "whose project is this,
 * and when is it due" gets asked. As a pane they stay put.
 *
 * IT STRETCHES. The pane resizes from 260 to 720, so the facts are a
 * two-column `auto-fit` grid rather than a fixed count - one column in a
 * narrow dock, two when it is pulled wide.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendarBlankLight,
  PiCircleDashedLight,
  PiClockCountdownLight,
  PiFlagLight,
  PiLinkSimpleLight,
  PiUsersThreeLight,
} from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import { Field, Well } from '../../shared/brand/Field';
import ProfileImage from '../../Header/ProfileImage';
import { formatDateTime } from '../../../utils/functions';
import {
  dueCountdown,
  dueTone,
  priorityColorMap,
  statusColorMap,
  titleCase,
} from '../projectFacts';
import { ContextSection } from '../../shared/brand/ContextPane';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import { normalise } from '../../shared/access/accessModel';

interface Props {
  project: any;
  /**
   * Raises the ACCESS PANE - not the manage side panel.
   *
   * This section used to hold an "N members" button that opened the
   * transactional editor over the page, which meant a page had four ways to
   * the same 50%-wide panel and no way to simply LOOK at the list. Reading is
   * the pane's job now; the pane's own footer button is the one way to write.
   */
  onOpenAccess: () => void;
  /** Switches the canvas to the Resources tab. */
  onOpenResources: () => void;
}

const ProjectContextPane: React.FC<Props> = ({
  project,
  onOpenAccess,
  onOpenResources,
}) => {
  const { t } = useTranslation();

  if (!project) return null;

  const tone = dueTone(project.due_date);
  const countdown = dueCountdown(project.due_date);
  const members = project.shared_with?.length || 0;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-3 space-y-4">
      {/* Overview - the two-word answers, side by side. */}
      <div>
        <ContextSection>{t('Overview')}</ContextSection>
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
        >
          <Field icon={<PiCircleDashedLight />} label={t('Status')}>
            <Badge color={statusColorMap[project.status] || 'gray'}>
              {project.status ? titleCase(project.status) : t('Active')}
            </Badge>
          </Field>
          <Field icon={<PiFlagLight />} label={t('Priority')}>
            {project.priority ? (
              <Badge color={priorityColorMap[project.priority] || 'gray'}>
                {titleCase(project.priority)}
              </Badge>
            ) : null}
          </Field>
        </div>
      </div>

      {/* Timeline - the due date carries the urgency colour, nothing else. */}
      <div>
        <ContextSection>{t('Timeline')}</ContextSection>
        <Well className="space-y-3">
          <Field
            icon={<PiClockCountdownLight />}
            label={t('Due date')}
            tone={tone}
            value={project.due_date ? formatDateTime(project.due_date) : undefined}
          />
          {countdown && (
            <p
              className={
                tone === 'danger'
                  ? 'text-[11px] font-medium text-danger-500 dark:text-danger-400 -mt-2'
                  : 'text-[11px] font-medium text-warning-600 dark:text-warning-400 -mt-2'
              }
            >
              {countdown}
            </p>
          )}
          <Field
            icon={<PiCalendarBlankLight />}
            label={t('Created')}
            value={project.created_at ? formatDateTime(project.created_at) : undefined}
            emptyText="-"
          />
        </Well>
      </div>

      {/* People - the owner, and the way into managing everyone else. */}
      <div>
        <ContextSection>{t('People')}</ContextSection>
        <Well className="space-y-3">
          {project.owner && (
            <Field label={t('Owner')}>
              <span className="flex items-center gap-2">
                <ProfileImage
                  profilePicture={normalise(project.owner).avatar}
                  email={project.owner.email || ''}
                  initials={project.owner.initials || '??'}
                  name={project.owner.name}
                  size="w-6 h-6"
                />
                <span className="text-sm text-fg-body truncate">{project.owner.name}</span>
              </span>
            </Field>
          )}
          <Field icon={<PiUsersThreeLight />} label={t('Access')}>
            {members > 0 ? (
              <CollaboratorStack
                collaborators={project.shared_with || []}
                onClick={onOpenAccess}
                testId="project-context-access-stack"
              />
            ) : (
              <span className="text-sm text-fg-muted">{t('No one has access yet')}</span>
            )}
          </Field>
        </Well>
      </div>

      {/* One count, and the way to the tab that lists them. */}
      <div>
        <ContextSection>{t('Linked')}</ContextSection>
        <Well className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-fg-body">
            <PiLinkSimpleLight className="h-4 w-4 text-fg-muted" />
            {project.total_resources || 0} {t('resources')}
          </span>
          <Button variant="neutral" size="small" onClick={onOpenResources}>
            {t('View')}
          </Button>
        </Well>
      </div>
    </div>
  );
};

export default ProjectContextPane;
